// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * One maintenance record on a page of its own, so a link to it can be pasted elsewhere
 * (design doc §10.8) and opened straight onto that record.
 *
 * The list opens records in place, which is the right way to read a history; a link from a
 * ticket is about one record, which may be pages down the list. The detail is the same
 * component the list expands, fetched under the same key, so opening it here after the
 * list (or the other way round) asks the server once.
 */

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { api } from "@/api/endpoints";
import { ChangeSummary } from "@/components/ChangeType";
import { CopyButton } from "@/components/CopyButton";
import { ErrorBanner } from "@/components/ErrorBanner";
import { JiraLink } from "@/components/JiraLink";
import { MaintenanceCorrection } from "@/components/MaintenanceCorrection";
import { EventDetail } from "@/components/MaintenanceEventDetail";
import { maintenanceEventLink } from "@/links";
import type { ArchiveContext } from "@/pages/ArchiveDetailPage";

export function MaintenanceEventPage() {
  const { t } = useTranslation();
  const { archiveId = "", eventId = "" } = useParams();
  const navigate = useNavigate();
  const currentVersion = useOutletContext<ArchiveContext | undefined>()?.archive.current_version;
  const event = useQuery({
    queryKey: ["maintenance-event", archiveId, eventId],
    queryFn: () => api.maintenance.get(archiveId, eventId),
    // An event never changes once written (§7.6).
    staleTime: Number.POSITIVE_INFINITY,
  });

  return (
    <>
      <div className="filters">
        <Link to={`/archives/${archiveId}/maintenance`}>{t("maintenance.backToList")}</Link>
      </div>
      <ErrorBanner error={event.error} />
      {event.isLoading && <p className="muted">{t("common.loading")}</p>}
      {event.data && (
        <div className="panel maintenance-event">
          <h2>
            {t("maintenance.eventTitle", { n: event.data.sequence_no })}
            <CopyButton
              value={maintenanceEventLink(archiveId, event.data.event_id)}
              label={t("maintenance.copyLink")}
              text={t("common.copyLink")}
            />
          </h2>
          <p className="record-meta">
            {t("maintenance.occurredAt")}: {new Date(event.data.occurred_at).toLocaleString()}
            {" · "}
            {t("archive.version")}: v{event.data.base_version} → v{event.data.result_version}
            {event.data.snapshot_unchanged && (
              <span className="badge badge-unchanged">{t("maintenance.unchangedBadge")}</span>
            )}
          </p>
          <p className="record-meta">
            {t("maintenance.changeType")}:{" "}
            <ChangeSummary changeType={event.data.change_type} reason={null} />
            {event.data.reason && (
              <>
                {" · "}
                {t("maintenance.reason")}: {event.data.reason}
              </>
            )}
          </p>
          {event.data.jira_url && (
            <p className="record-meta">
              {t("maintenance.jiraUrl")}: <JiraLink url={event.data.jira_url} />
            </p>
          )}
          <div className="filters">
            <MaintenanceCorrection
              archiveId={archiveId}
              event={event.data}
              currentVersion={currentVersion}
              onRevoked={() => navigate(`/archives/${archiveId}/maintenance`, { replace: true })}
            />
          </div>
          <EventDetail archiveId={archiveId} event={event.data} />
        </div>
      )}
    </>
  );
}
